import React, { useEffect, useState, useCallback, useLayoutEffect, useRef } from 'react';
import styles from './GlobalNotification.module.css';

/* ---------- Payload types ---------- */

export interface AdvancementPayload {
  kind: 'advancement';
  mercenaryId: number;
  mercenaryName: string;
  race: string;
  sex: string;
  avatar: number;
  advancementKey: string;
  advancementName: string;
  advancementDescription: string;
  earnedAt: string;
}

export interface AnnouncementPayload {
  kind: 'announcement';
  type: 'tournament_scheduled' | 'tournament_locked' | 'tournament_completed' | 'new_mercenary';
  title: string;
  message: string;
  metadata?: {
    tournamentId?: number;
    tournamentName?: string;
    mercenaryName?: string;
    race?: string;
    sex?: string;
    avatar?: number;
  };
  timestamp: string;
}

export type NotificationPayload = AdvancementPayload | AnnouncementPayload;

type QueueEntry = NotificationPayload & { uid: string };

interface GlobalNotificationProps {
  events: NotificationPayload[];
}

/* ---------- Sub-label map for announcement types ---------- */

const ANNOUNCEMENT_LABELS: Record<string, string> = {
  tournament_scheduled: 'Tournament',
  tournament_locked: 'Tournament',
  tournament_completed: 'Tournament',
  new_mercenary: 'New Arrival',
};

/* ---------- Component ---------- */

const GlobalNotification: React.FC<GlobalNotificationProps> = ({ events }) => {
  const [queue, setQueue] = useState<QueueEntry[]>([]);
  const [current, setCurrent] = useState<QueueEntry | null>(null);
  const [phase, setPhase] = useState<'entering' | 'visible' | 'leaving'>('entering');
  const [topOffset, setTopOffset] = useState(80);
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);

  const clearTimers = useCallback(() => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  }, []);

  /* Measure header so we sit directly below it */
  useLayoutEffect(() => {
    const measure = () => {
      const header = document.querySelector('header');
      if (header && header.getBoundingClientRect().height > 0) {
        setTopOffset(header.getBoundingClientRect().bottom + 10);
      } else {
        const mobileHeader = document.querySelector('[class*="mobileHeader"]') as HTMLElement | null;
        if (mobileHeader) {
          setTopOffset(mobileHeader.getBoundingClientRect().bottom + 10);
        }
      }
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  /* Push new events into the queue */
  useEffect(() => {
    if (events.length === 0) return;
    const latest = events[events.length - 1];

    const uid =
      latest.kind === 'advancement'
        ? `adv-${latest.mercenaryId}-${latest.advancementKey}-${latest.earnedAt}`
        : `ann-${latest.type}-${latest.timestamp}-${latest.title}`;

    setQueue((prev) => {
      // Prevent duplicate queue entries when reconnects/resends happen.
      if (prev.some((entry) => entry.uid === uid)) return prev;
      const next = [...prev, { ...latest, uid } as QueueEntry];
      return next.length > 50 ? next.slice(-50) : next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  const advanceQueue = useCallback(() => {
    setQueue((prev) => prev.slice(1));
    setCurrent(null);
  }, []);

  const dismissCurrent = useCallback(() => {
    clearTimers();
    setPhase('leaving');
    const t = setTimeout(advanceQueue, 700);
    timers.current.push(t);
  }, [advanceQueue, clearTimers]);

  /* Pick next from queue when idle */
  useEffect(() => {
    if (current !== null || queue.length === 0) return;
    setCurrent(queue[0]);
    setPhase('entering');
  }, [current, queue]);

  /* Drive animation phases */
  useEffect(() => {
    if (!current) return;

    const displayTime = current.kind === 'advancement' ? 15000 : 10000;

    const enterTimer = setTimeout(() => {
      setPhase('visible');

      const visibleTimer = setTimeout(() => {
        setPhase('leaving');

        const leaveTimer = setTimeout(() => {
          advanceQueue();
        }, 700);
        timers.current.push(leaveTimer);
      }, displayTime);
      timers.current.push(visibleTimer);
    }, 50);
    timers.current.push(enterTimer);

    return () => { clearTimers(); };
  }, [current, advanceQueue, clearTimers]);

  if (!current) return null;

  /* ---------- Render ---------- */

  if (current.kind === 'advancement') {
    const avatarSrc = `/img/avatars/${current.race?.toLowerCase() ?? 'human'}.${current.sex?.toLowerCase() ?? 'male'}.${current.avatar ?? 1}.png`;

    return (
      <div
        className={`${styles.wrapper} ${
          phase === 'entering' ? styles.entering :
          phase === 'visible'  ? styles.visible  :
                                 styles.leaving
        }`}
        style={{ '--top-offset': `${topOffset}px`, cursor: 'pointer' } as React.CSSProperties}
        role="status"
        aria-live="polite"
        onClick={dismissCurrent}
      >
        <div className={`${styles.card} ${styles.advancementCard}`}>
          <img className={styles.avatar} src={avatarSrc} alt={current.mercenaryName} />
          <div className={styles.text}>
            <span className={styles.primaryText}>{current.mercenaryName}</span>
            <span className={styles.secondaryText}>{current.advancementName}</span>
            <span className={styles.tertiaryText}>{current.advancementDescription}</span>
          </div>
          <div className={styles.badge}>
            <span className={styles.badgeLabel}>Advancement</span>
          </div>
        </div>
      </div>
    );
  }

  /* Announcement card */
  const showAvatar =
    current.type === 'new_mercenary' && current.metadata?.race && current.metadata?.sex;
  const avatarSrc = showAvatar
    ? `/img/avatars/${current.metadata!.race!.toLowerCase()}.${current.metadata!.sex!.toLowerCase()}.${current.metadata!.avatar ?? 1}.png`
    : null;

  return (
    <div
      className={`${styles.wrapper} ${
        phase === 'entering' ? styles.entering :
        phase === 'visible'  ? styles.visible  :
                               styles.leaving
      }`}
      style={{ '--top-offset': `${topOffset}px`, cursor: 'pointer' } as React.CSSProperties}
      role="status"
      aria-live="polite"
      onClick={dismissCurrent}
    >
      <div className={`${styles.card} ${styles.announcementCard}`}>
        {avatarSrc ? (
          <img className={styles.avatar} src={avatarSrc} alt={current.metadata?.mercenaryName ?? ''} />
        ) : (
          <div className={styles.iconPlaceholder} />
        )}
        <div className={styles.text}>
          <span className={styles.primaryText}>{current.title}</span>
          <span className={styles.secondaryText}>{current.message}</span>
        </div>
        <div className={styles.badge}>
          <span className={styles.badgeLabel}>{ANNOUNCEMENT_LABELS[current.type] ?? 'Announcement'}</span>
        </div>
      </div>
    </div>
  );
};

export default GlobalNotification;
